import { routerReducer, RouterState } from '@ngrx/router-store';
import { Action, ActionReducer, ActionReducerMap, createAction, MetaReducer, State } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';
import { environment } from '../environments/environment';
import { applicationsFeatureKey, applicationsReducer, State as ApplicationsState } from './store/applications/applications.reducer';
import {
  accountCompletionFeatureKey,
  accountCompletionReducer,
  State as AccountCompletionState,
} from './store/account-completion/account-completion.reducer';
import {
  dashboardKpisFeatureKey,
  dashboardKpisReducer,
  State as DashboardKpisState,
} from './store/dashboard-kpis/dashboard-kpis.reducer';
import {
  performanceAnalysisFeatureKey,
  performanceAnalysisReducer,
  State as PerformanceAnalysisState,
} from './store/performance-analysis/performance-analysis.reducer';
import { authFeatureKey, authReducer, State as AuthState } from './store/auth/auth.reducer';
import {
  companyAccountFeatureKey,
  companyAccountReducer,
  State as CompanyAccountState,
} from './store/company-account/company-account.reducer';
import { companyIconFeatureKey, companyIconReducer } from './store/company-icon/company-icon.reducer';
import { companyLogoFeatureKey, companyLogoReducer, State as CompanyLogoState } from './store/company-logo/company-logo.reducer';
import { configFeatureKey, configReducer, State as ConfigState } from './store/config/config.reducer';
import { connectionFeatureKey, connectionReducer, State as ConnectionState } from './store/connection/connection.reducer';
import { creditsFeatureKey, creditsReducer, State as CreditsState } from './store/credits/credits.reducer';
import {
  companyRedFlagsFeatureKey,
  companyRedFlagsReducer,
  State as CompanyRedFlagsState,
} from './store/company-red-flags/company-red-flags.reducer';
import {
  declineReasonsFeatureKey,
  declineReasonsReducer,
  State as DeclineReasonsState,
} from './store/decline-reasons/decline-reasons.reducer';
import { designFeatureKey, designReducer } from './store/design/design.reducer';
import { emailReducer, emailsFeatureKey, State as EmailState } from './store/emails/emails.reducer';
import {
  exportDownloadsFeatureKey,
  exportDownloadsReducer,
  State as ExportDownloadsState,
} from './store/export-downloads/export-downloads.reducer';
import { facilitiesFeatureKey, State as FacilitiesState, facilityReducer } from './store/facilities/facilities.reducer';
import { jobsFeatureKey, jobsReducer, State as JobsState } from './store/jobs/jobs.reducer';
import {
  notificationSettingsFeatureKey,
  notificationSettingsReducer,
  State as NotificationSettingsState,
} from './store/notification-settings/notification-settings.reducer';
import {
  notificationsFeatureKey,
  notificationsReducer,
  State as NotificationsState,
} from './store/notifications/notifications.reducer';
import { paymentFeatureKey, paymentReducer } from './store/payment/payment.reducer';
import { signatureReducer, signaturesFeatureKey, State as SignatureState } from './store/signatures/signatures.reducer';
import { subscriptionFeatureKey, subscriptionReducer, State as SubscriptionState } from './store/subscription/subscription.reducer';
import { tfaMethodsFeatureKey, tfaMethodsReducer } from './store/tfa-methods/tfa-methods.reducer';
import { todosFeatureKey, todosReducer, State as TodosState } from './store/todos/todos.reducer';
import { userAccountFeatureKey, userAccountReducer, State as UserAccountState } from './store/user-account/user-account.reducer';
import { userAvatarFeatureKey, userAvatarReducer } from './store/user-avatar/user-avatar.reducer';
import { usersFeatureKey, usersReducer, State as UsersState } from './store/users/users.reducer';

export interface AppState {
  router: RouterState;
  [applicationsFeatureKey]?: ApplicationsState;
  [accountCompletionFeatureKey]?: AccountCompletionState;
  [dashboardKpisFeatureKey]?: DashboardKpisState;
  [performanceAnalysisFeatureKey]?: PerformanceAnalysisState;
  [authFeatureKey]: AuthState;
  [companyAccountFeatureKey]?: CompanyAccountState;
  [companyIconFeatureKey]?: unknown;
  [companyLogoFeatureKey]?: CompanyLogoState;
  [configFeatureKey]: ConfigState;
  [connectionFeatureKey]?: ConnectionState;
  [designFeatureKey]?: unknown;
  [emailsFeatureKey]?: EmailState;
  [facilitiesFeatureKey]?: FacilitiesState;
  [jobsFeatureKey]?: JobsState;
  [notificationSettingsFeatureKey]?: NotificationSettingsState;
  [notificationsFeatureKey]?: NotificationsState;
  [paymentFeatureKey]?: unknown;
  [signaturesFeatureKey]?: SignatureState;
  [subscriptionFeatureKey]?: SubscriptionState;
  [tfaMethodsFeatureKey]?: unknown;
  [todosFeatureKey]?: TodosState;
  [userAccountFeatureKey]?: UserAccountState;
  [userAvatarFeatureKey]?: unknown;
  [usersFeatureKey]?: UsersState;
  [declineReasonsFeatureKey]?: DeclineReasonsState;
  [creditsFeatureKey]?: CreditsState;
  [exportDownloadsFeatureKey]?: ExportDownloadsState;
  [companyRedFlagsFeatureKey]?: CompanyRedFlagsState;
}

/** Root reducers for StoreModule.forRoot (legacy) - used when feature modules still use NgModules */
export const reducers: ActionReducerMap<AppState> = {
  router: routerReducer,
  [authFeatureKey]: authReducer,
  [configFeatureKey]: configReducer,
};

/** Full reducers map for standalone bootstrap (provideStore) - includes all feature states */
export const fullReducers: ActionReducerMap<AppState> = {
  router: routerReducer,
  [authFeatureKey]: authReducer,
  [configFeatureKey]: configReducer,
  [applicationsFeatureKey]: applicationsReducer,
  [accountCompletionFeatureKey]: accountCompletionReducer,
  [dashboardKpisFeatureKey]: dashboardKpisReducer,
  [performanceAnalysisFeatureKey]: performanceAnalysisReducer,
  [companyAccountFeatureKey]: companyAccountReducer,
  [companyIconFeatureKey]: companyIconReducer,
  [companyLogoFeatureKey]: companyLogoReducer,
  [connectionFeatureKey]: connectionReducer,
  [designFeatureKey]: designReducer,
  [emailsFeatureKey]: emailReducer,
  [facilitiesFeatureKey]: facilityReducer,
  [jobsFeatureKey]: jobsReducer,
  [notificationSettingsFeatureKey]: notificationSettingsReducer,
  [notificationsFeatureKey]: notificationsReducer,
  [paymentFeatureKey]: paymentReducer,
  [signaturesFeatureKey]: signatureReducer,
  [subscriptionFeatureKey]: subscriptionReducer,
  [tfaMethodsFeatureKey]: tfaMethodsReducer,
  [todosFeatureKey]: todosReducer,
  [userAccountFeatureKey]: userAccountReducer,
  [userAvatarFeatureKey]: userAvatarReducer,
  [usersFeatureKey]: usersReducer,
  [declineReasonsFeatureKey]: declineReasonsReducer,
  [creditsFeatureKey]: creditsReducer,
  [exportDownloadsFeatureKey]: exportDownloadsReducer,
  [companyRedFlagsFeatureKey]: companyRedFlagsReducer,
};

export const clearStore = createAction('[General] Clear store');

export function clearReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return function (state, action) {
    if (action.type === clearStore.type) {
      return reducer(
        {
          [configFeatureKey]: state[configFeatureKey],
          [authFeatureKey]: state[authFeatureKey],
        },
        action
      );
    } else {
      return reducer(state, action);
    }
  };
}

export const localStorageSyncReducer =
  (reducer: ActionReducer<any>): ActionReducer<any> =>
  (state: State<any>, action: Action): any => {
    const keys = [authFeatureKey, configFeatureKey, jobsFeatureKey, exportDownloadsFeatureKey];

    const syncedState = localStorageSync({
      keys,
      rehydrate: true,
    })(reducer)(state, action);

    if (!state && syncedState?.[authFeatureKey]) {
      return {
        ...syncedState,
        [authFeatureKey]: {
          ...syncedState[authFeatureKey],
          showReleaseNotesAfterLogin: false,
        },
      };
    }

    return syncedState;
  };

export const metaReducers: MetaReducer<AppState>[] = !environment.production
  ? [clearReducer, localStorageSyncReducer]
  : [clearReducer, localStorageSyncReducer];
